/**
 * Einstiegspunkt der Portal-Shell.
 *
 * Hier — und nur hier — lebt das Framework. ADR-016 hält die Grenze fest:
 * `packages/contracts`, `packages/domain/**` und `packages/sync` bleiben frei
 * von React, HTTP-Framework und Node-APIs, damit eine spätere native App aus
 * `02` §2.13 denselben Code wiederverwenden kann. ADR-043 nennt dieselbe
 * Grenze als Rückweg: Wer sie hält, tauscht bei einem Wechsel den
 * Einstiegspunkt, nicht die Anwendung.
 */

import { StrictMode, useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router";

import { type PortalKonfiguration, OHNE_KONFIGURATION } from "@hwc/config";
import {
  cssVariablen,
  Fehlermeldung,
  Hinweis,
  HINWEISARTEN,
  Knopf,
  KNOPFARTEN,
  Ladezustand,
  LADEARTEN,
  Zustand,
  ZUSTANDSARTEN,
  type Erscheinung,
} from "@hwc/ui";

import { setzeAustauschadresse } from "@hwc/demo-data";

import { ladeKonfiguration } from "./konfiguration.js";
import { Anmeldetor, type Anmeldelage } from "./anmeldung/Anmeldetor.js";
import { AngemeldetAls } from "./anmeldung/Angemeldet.js";
import { rollenAusGruppen } from "@hwc/auth";

import { Dashboard } from "../features/dashboard/Dashboard.js";
import { Auftragsakte } from "../features/jobs/Auftragsakte.js";
import { Auftragsliste } from "../features/jobs/Auftragsliste.js";
import { Kundenakte, Kundenliste } from "../features/customers/Kunden.js";
import { Eingang } from "../features/inbox/Eingang.js";
import { Suche } from "../features/search/Suche.js";
import { Wartung } from "../features/maintenance/Wartung.js";
import { ERechnungen } from "../features/einvoice/ERechnungen.js";
import { Uebergabe } from "../features/handoff/Uebergabe.js";
import {
  Administration,
  Assistent,
  Aufgaben,
  Dateien,
  Dokumente,
  Kalender,
  Kommunikation,
  Wissen,
} from "./bereiche/Weitere.js";
import { Personenwahl } from "./daten/Personenwahl.js";
import { NeuerAuftragImDialog } from "./daten/NeuerAuftrag.js";
import { SitzungsRahmen, useSitzung } from "./daten/Sitzung.js";

import { Rahmenansicht } from "./rahmen/Rahmenansicht.js";
import {
  anfangsWahl,
  beiGeraetewechsel,
  merken,
  vomGeraet,
  wirksame,
  type Wahl,
} from "./shell/erscheinung.js";
import { sichtbareNavigation } from "./shell/navigation.js";
import {
  dienstadresse,
  laesstSichRahmen,
  oberflaecheZu,
  quelladresse,
  setzeDienstadressen,
} from "@hwc/demo-data";
import { Shell } from "./shell/Shell.js";
import "@hwc/ui/bauteile.css";
import "./stil.css";

/** Wo die Attrappen aus `tests/ui/attrappen` liegen. */
// `127.0.0.1` statt `localhost`: Der Port zaehlt fuer Cookies nicht zur
// Herkunft, zwei Ports auf `localhost` waeren also same-site. Erst mit einem
// anderen Host rahmt die Shell eine echte Fremdherkunft — und nur dann misst
// die Sitzungspruefung in `tests/ui/README.md` das, was sie behauptet.
const ATTRAPPEN = "http://127.0.0.1:4310";

/** Die sechs Prüfungen aus `tests/ui/README.md`, hier bedienbar gemacht. */
const PROBEN: Record<string, { name: string; pfad: string; meldetSich: boolean }> = {
  // Alle Attrappen senden das Lebenszeichen. Deshalb ist der Hinweis der
  // Shell hier eine Aussage und keine Zeitmessung: Er erscheint bei
  // `/verweigert` und bei keiner anderen.
  verweigert: { name: "Framing verweigert", pfad: "/verweigert", meldetSich: true },
  sitzung: { name: "Sitzung im Rahmen", pfad: "/sitzung", meldetSich: true },
  tastatur: { name: "Tastatur", pfad: "/tastatur", meldetSich: true },
  tiefverweis: { name: "Tiefverweis", pfad: "/tiefverweis?melden=1", meldetSich: true },
  lang: { name: "Eine Höhe", pfad: "/lang", meldetSich: true },
};


function Platzhalter({ was }: { was: string }) {
  return (
    <>
      <h1>{was}</h1>
      <p className="leise">
        Diese Ansicht entsteht in einem späteren Paket. Der Punkt steht schon in
        der Navigation, weil <code>07</code> §7.3 ihn dort vorsieht — ein Menü,
        das später wächst, verschiebt jede Gewöhnung.
      </p>
    </>
  );
}

/**
 * Eine Ansicht, die absichtlich abstürzt — der Gegenstand für das Prüfrezept.
 *
 * Aus demselben Grund wie die Rahmenproben daneben: Eine Fehlergrenze, deren
 * Wirkung niemand auslösen kann, ist eine Zusage im Kommentar. Sie steht
 * nicht in der Navigation und ist nur über die Adresse erreichbar.
 */
function Fehlerprobe(): never {
  throw new Error(
    // Absichtlich mit einem Dateinamen darin: Die Prüfung misst, dass davon
    // nichts auf dem Bildschirm landet (`04` §4.6).
    'Absichtlicher Fehler der Fehlerprobe: Angebot_Mueller_2026.pdf',
  );
}

function Proben() {
  return (
    <>
      <h1>Proben</h1>
      <p>
        Werkzeugseite, kein Bereich des Produkts. Sie steht nicht in der
        Navigation und ist nur über die Adresse erreichbar — hier liegen die
        Gegenstände, an denen sich das Prüfrezept aus <code>tests/ui/README.md</code>
        festmachen lässt: die Rahmenansicht für eingebettete Fremdoberflächen
        nach ADR-040 und die absichtlich abstürzende Ansicht für die
        Fehlergrenze.
      </p>
      <p>
        <a href="/bauteile">Bauteile des Designsystems</a> — Knöpfe, Zustände
        und Ladeanzeigen in allen Ausprägungen.
      </p>

      <h2>Rahmenproben</h2>
      <p className="leise">
        Die Attrappen dazu laufen getrennt:{" "}
        <code>node tests/ui/attrappen/server.cjs</code>. Das Rezept steht in{" "}
        <code>tests/ui/README.md</code>.
      </p>
      <ul>
        {Object.entries(PROBEN).map(([schluessel, probe]) => (
          <li key={schluessel}>
            <a href={`/rahmenprobe/${schluessel}`}>{probe.name}</a>
          </li>
        ))}
      </ul>
    </>
  );
}

function Rahmenprobe() {
  const { welche } = useParams();
  const [suche, setzeSuche] = useSearchParams();
  const probe = welche ? PROBEN[welche] : undefined;

  // Der Ort steht in der Adresse der Shell, nicht im Zustand des Bauteils:
  // Nur so landen Neuladen und Lesezeichen an derselben Stelle (Prüfung 4).
  const ort = suche.get("ort");
  const beiOrt = useCallback(
    (neu: string) => setzeSuche({ ort: neu }, { replace: true }),
    [setzeSuche],
  );

  if (!probe) return <Platzhalter was="Unbekannte Rahmenprobe" />;

  return (
    <>
      <h1>{probe.name}</h1>
      <Rahmenansicht
        name={probe.name}
        adresse={ATTRAPPEN + probe.pfad}
        ort={ort}
        beiOrt={beiOrt}
        meldetSich={probe.meldetSich}
      />
    </>
  );
}

/**
 * Die Bauteile in allen Zuständen, auf einer Seite.
 *
 * Zwei Zwecke, und der zweite ist der wichtigere: Sie ist die Fläche, an der
 * das Prüfrezept die Regeln aus `07` §7.14 **misst** statt sie zu behaupten —
 * Trefferflächen, Text an jedem Zustand, benannte Ladezustände. Ohne eine
 * solche Seite müsste jede Prüfung sich ihre Beispiele selbst bauen und würde
 * damit etwas anderes prüfen als das, was im Portal erscheint.
 *
 * Später bettet `apps/handbook` nach ADR-016 dieselben Bauteile ein, statt
 * Bildschirmfotos zu pflegen.
 */
function Bauteilprobe() {
  return (
    <>
      <h1>Bauteile</h1>

      <section className="karte">
      <h2>Knöpfe</h2>
      <p className="leise">
        Trefferfläche aus den Token: {"≥"} 44 Pixel, für Hauptaktionen
        eines Einsatzes {"≥"} 56.
      </p>
      <p style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {KNOPFARTEN.map((art) => (
          <Knopf key={art} type="button" art={art}>{art}</Knopf>
        ))}
        <Knopf type="button" art="haupt" gross>Einsatz beginnen</Knopf>
        <Knopf type="button" art="neben" disabled>nicht möglich</Knopf>
      </p>

      </section>

      <section className="karte">
      <h2>Zustände</h2>
      <p className="leise">
        Jeder trägt Text und ein Zeichen — <code>07</code> §7.14 schließt
        farbabhängige Statusinformation aus.
      </p>
      <ul>
        {ZUSTANDSARTEN.map((art) => (
          <li key={art}>
            <Zustand art={art} text={`Zustand ${art}`} />
          </li>
        ))}
        <li>
          <Zustand art="warnung" text="Foto nicht übertragen" kinder="seit 12 Minuten" />
        </li>
      </ul>

      </section>

      <section className="karte">
      <h2>Hinweise</h2>
      <p className="leise">
        Für den Fall, dass etwas <em>fehlt oder eingeschränkt</em> ist, ohne
        dass ein Fehler vorliegt — die Oberfläche läuft weiter. Der fett
        gesetzte Anfang ist Pflicht: Eine farbige Kante allein wäre
        farbabhängige Statusinformation.
      </p>
      {HINWEISARTEN.map((art) => (
        <Hinweis key={art} art={art} titel={`Hinweis ${art}.`}>
          Der Fließtext trägt die Auskunft und darf einen{" "}
          <a href="/bauteile">Verweis</a> enthalten.
        </Hinweis>
      ))}

      </section>

      <section className="karte">
      <h2>Lade- und Synchronisationszustände</h2>
      <p className="leise">
        Benannt, nicht angedeutet: Ein Monteur muss wissen, ob seine Eingabe
        beim Büro ist oder noch auf dem Gerät liegt (<code>07</code> §7.9).
      </p>
      {LADEARTEN.map((art) => (
        <Ladezustand
          key={art}
          art={art}
          {...(art === "wartet" ? { hinweis: "wird gesendet, sobald Netz da ist" } : {})}
        />
      ))}
      </section>

      <section className="karte">
      <h2>Fehler</h2>
      <p className="leise">
        Die Beispiele tragen absichtlich heikle Felder — einen Stacktrace, ein
        Tokenfragment, einen Bucketnamen. <code>04</code> §4.6 verbietet, sie
        an die Oberfläche zu geben; sichtbar ist deshalb nur unser eigener Text
        und die Kennung für Rückfragen. Eine Browserprüfung misst das.
      </p>
      <Fehlermeldung
        fehler={{
          code: "RESOURCE_FORBIDDEN",
          message: "nextcloud sagte: 403 on bucket hwc-germis-nc/files/2481",
          retryable: false,
          provider: "nextcloud",
          correlationId: "01JC7X4E9QZ2M0V3",
          details: { stack: "at Adapter.hole (nextcloud.ts:214)", token: "eyJhbGciOi" },
        }}
      />
      <Fehlermeldung
        fehler={{
          code: "PROVIDER_UNAVAILABLE",
          message: "connect ETIMEDOUT 10.0.0.4:9980",
          retryable: true,
          provider: "collabora",
          correlationId: "01JC7X4F1A8B2C3D",
        }}
        beiWiederholen={() => undefined}
      />
      </section>
    </>
  );
}

/**
 * Was bei einer Adresse erscheint, die es nicht gibt.
 *
 * Vorher blieb die Fläche unter dem Header leer — die Shell stand, der Inhalt
 * fehlte, und nichts sagte warum. Das trifft nicht nur Tippfehler: Ein Punkt,
 * den ein abgeschaltetes Merkmal verbirgt, ist über ein altes Lesezeichen
 * weiterhin ansteuerbar, und dann ist genau diese Ansicht die Antwort.
 *
 * Sie nennt deshalb beide möglichen Gründe, ohne zu behaupten, welcher
 * vorliegt: Das Portal weiß es an dieser Stelle nicht.
 */
function Unbekannt() {
  return (
    <>
      <h1>Diese Ansicht gibt es hier nicht</h1>
      <p>
        Entweder stimmt die Adresse nicht, oder der Bereich ist in diesem
        Betrieb nicht eingerichtet — abschaltbare Bereiche erscheinen nur, wenn
        das zugehörige Merkmal an ist.
      </p>
      <p>
        <a href="/">Zurück zur Übersicht</a>
      </p>
    </>
  );
}

/**
 * Der Hinweis aus Auflage 3 (ADR-044): sichtbar, aber nicht im Weg.
 *
 * Die Shell läuft ohne Konfiguration weiter — mit den Vorgabewerten der Token.
 * Verschweigen wäre falsch: Ohne Domains fehlen Verweise, ohne Merkmale ist
 * alles aus. Wer das sieht, soll wissen, warum.
 */
/**
 * Der Hinweis auf eine unvollstaendige Laufzeitkonfiguration (ADR-044,
 * Auflage 3).
 *
 * **Das `data`-Attribut ist kein Testhaken, sondern eine Unterscheidung.**
 * Bis zum 05.09.2026 war dies der einzige Hinweis im Portal, und die
 * Oberflaechenpruefung konnte ihn ueber `.hinweis` ansprechen. Seit das
 * Dashboard auch fachliche Hinweise zeigt — ein abwesender Monteur mit
 * zugewiesenen Einsaetzen —, misst dieselbe Abfrage zwei verschiedene Dinge
 * und schlaegt an, wenn zufaellig jemand krank ist.
 *
 * Die beiden haben nichts miteinander zu tun: Der eine sagt, dass die
 * Anwendung unvollstaendig konfiguriert ist, der andere, dass im Betrieb etwas
 * los ist. Sie zu trennen ist deshalb richtig, unabhaengig von der Pruefung.
 */
function Konfigurationshinweis({ text }: { text: string }) {
  return (
    <div data-hinweis="konfiguration">
    <Hinweis titel="Eingeschränkte Ansicht.">
      {text} Die Oberfläche läuft mit Vorgabewerten; Verweise auf andere
      Dienste und abschaltbare Bereiche fehlen möglicherweise.
    </Hinweis>
    </div>
  );
}

/**
 * Das Band, das sagt: Diese Daten sind erfunden.
 *
 * **`10` §10.8 verlangt es wörtlich.** „Für den Infrastruktur-Pilot dürfen
 * noch nicht implementierte Eigenmodule durch **klar gekennzeichnete**
 * Testseiten ersetzt werden." Die Oberfläche läuft, die Adapter nicht — was
 * sie zeigt, kommt aus `packages/demo-data`.
 *
 * **Warum das mehr ist als Ordnung.** Auf `pilot.<kunde>` sieht ein Portal mit
 * „Bäckerei Hufnagel GmbH", „Auftrag 2481" und einer Telefonnummer aus wie ein
 * Betrieb. Die Namen sind erfunden und die Nummern auch — aber das sieht man
 * ihnen nicht an, und jemand ruft an oder trifft eine Entscheidung darauf.
 * Eine Attrappe, die für echt gehalten wird, ist schlimmer als gar keine.
 *
 * **Nur mit Laufzeitkonfiguration.** Lokal beim Entwickeln weiß jeder, woran
 * er ist; dort wäre das Band Lärm, und Lärm wird weggeklickt. Sobald ein
 * `stackName` kommt, läuft die Oberfläche bei einem Kunden — und dann gehört
 * der Satz hin.
 *
 * Er verschwindet, sobald die Adapter echte Daten liefern. Bis dahin steht er
 * über jeder Seite und nicht nur auf der Übersicht: Wer über einen Link
 * mitten in eine Auftragsakte springt, sieht die Startseite nie.
 */
function Demoband({ stackName }: { stackName: string }) {
  return (
    <div data-hinweis="demodaten">
      <Hinweis titel="Erfundene Daten.">
        Diese Oberfläche läuft im Stack <strong>{stackName}</strong>, zeigt aber
        den Demonstrationsstand aus <code>DEMO-001</code> — Betrieb, Kunden,
        Aufträge und Telefonnummern sind <strong>ausgedacht</strong>. Die
        Fachadapter zu Nextcloud, Vikunja, Stalwart und Paperless sind noch
        nicht gebaut; bis dahin ist hier nichts echt außer der Bedienung.
      </Hinweis>
    </div>
  );
}

/**
 * Welche Ansicht hinter welchem Navigationspunkt liegt.
 *
 * Als Tabelle und nicht als Folge von `<Route>`-Zeilen, weil die Routen aus
 * `sichtbareNavigation` erzeugt werden — und das ist die Regel, die schon
 * vorher hier stand: Ein abgeschaltetes Merkmal, das ueber die Adresszeile
 * doch erreichbar bleibt, waere kein Versehen mehr, sondern eine Hintertuer.
 * Dieselbe Konfiguration, die den Punkt verbirgt, muss auch den Weg
 * schliessen.
 *
 * Ein Punkt ohne Eintrag hier bekommt weiterhin den Platzhalter. Das ist der
 * ehrliche Zwischenstand fuer einen Bereich, den noch niemand gebaut hat —
 * und er faellt auf, statt still zu fehlen.
 */

/**
 * Eine Ressource im Quellsystem — der Sprung aus der Auftragsakte.
 *
 * **Das Portal zeigt sie, es liefert sie nicht aus.** `01` §1.11: „ACL wird am
 * Quellsystem geprüft, nicht im Portal entschieden." Diese Ansicht kennt
 * deshalb nur die Adresse; ob etwas erscheint, entscheidet der Dienst — und
 * die Rahmenansicht ist darauf eingerichtet, dass das auch schiefgehen kann.
 *
 * **Gerahmt wird eine Attrappe**, solange `LNK-001` die Verweisregistry und
 * die Adapter ihre Dienste nicht gebracht haben. Das steht auch auf dem
 * Bildschirm: Ein Sprung, der aussieht wie Nextcloud und keiner ist, wäre die
 * teuerste Sorte Attrappe — man verlässt sich darauf und merkt beim Piloten,
 * dass die Hälfte fehlt.
 *
 * Die Kennung kommt aus der Adresse, alles Weitere aus dem Bestand. Ein
 * Portal, das Namen und Pfad aus der Adresszeile übernähme, ließe sich einen
 * beliebigen Text unterschieben.
 */
function Quelle() {
  const { kennung } = useParams();
  const { bestand } = useSitzung();
  const [suche, setzeSuche] = useSearchParams();

  /*
   * **Gesucht wird über alle Ressourcen, die ein Quellsystem haben.**
   *
   * Dateien liegen in Nextcloud, Mails in Stalwart, archivierte Dokumente in
   * Paperless. Für den Weg dorthin macht das keinen Unterschied — es ist
   * derselbe Rahmen, dieselbe Absage, derselbe Rückweg. Drei Ansichten dafür
   * wären dreimal dieselbe Entscheidung, an drei Stellen zu ändern.
   */
  const datei = bestand.ablage.find((o) => o.ref.opaqueId === kennung);
  const nachricht = bestand.nachrichten.find((n) => n.ref.opaqueId === kennung);
  // Ein Einsatz ist nach `04` §4.4 eine Vikunja-Aufgabe — und Vikunja ist der
  // einzige der vier Dienste, der sich rahmen lässt. Hier ist ADR-040 nicht
  // Absicht, sondern Wirklichkeit.
  const einsatz = bestand.einsaetze.find((e) => e.ref.opaqueId === kennung);

  const objekt = datei
    ? {
        ref: datei.ref,
        name: datei.name,
        pfad: datei.pfad,
        geloescht: datei.geloescht,
      }
    : nachricht
      ? {
          ref: nachricht.ref,
          name: nachricht.betreff,
          // Eine Mail hat keinen Pfad, sondern einen Absender. Der steht im
          // Rahmen an derselben Stelle — er beantwortet dieselbe Frage:
          // „Woher kommt das, was ich hier sehe?"
          pfad: `von ${nachricht.von}`,
          geloescht: undefined,
        }
      : einsatz
        ? {
            ref: einsatz.ref,
            name: einsatz.titel,
            pfad: undefined,
            geloescht: undefined,
          }
        : undefined;

  const ort = suche.get("ort");
  const beiOrt = useCallback(
    (neu: string) => setzeSuche({ ort: neu }, { replace: true }),
    [setzeSuche],
  );

  if (!objekt) {
    return (
      <>
        <h1>Nicht gefunden</h1>
        <p className="leise">
          Zur Kennung <code>{kennung}</code> gibt es in diesem Bestand nichts.
        </p>
      </>
    );
  }

  const system = oberflaecheZu(objekt.ref);
  const echt = dienstadresse(objekt.ref);
  const rahmbar = laesstSichRahmen(objekt.ref);
  const adresse = quelladresse(objekt.ref, {
    name: objekt.name,
    ...(objekt.pfad ? { pfad: objekt.pfad } : {}),
    ...(objekt.geloescht !== undefined ? { fall: "weg" as const } : {}),
  });

  return (
    <>
      <h1>{objekt.name}</h1>

      {echt ? (
        <p className="notiz" data-hinweis="echter-dienst">
          <strong>{system}</strong> läuft und ist erreichbar. Was fehlt, ist der
          Tiefverweis: Die Kennungen dieses Demobestands gibt es dort nicht, also
          führt der Weg in die Anwendung statt auf {objekt.name}. Das Dokument
          selbst erreicht der Adapter — <code>LNK-001</code> und der Dienstadapter.
        </p>
      ) : (
        <p className="notiz notiz--warnung" data-hinweis="attrappe">
          <strong>Das ist eine Attrappe, nicht {system}.</strong> Der echte Sprung
          braucht die Verweisregistry aus <code>LNK-001</code> und den Adapter des
          Dienstes. Was hier geprüft werden kann, ist der Weg: Rahmen, Fokus,
          Rückweg und die Absage, wenn das Quellsystem nicht ausliefert.
        </p>
      )}

      {rahmbar ? (
        <Rahmenansicht
          name={system}
          adresse={adresse}
          ort={ort}
          beiOrt={beiOrt}
          meldetSich={!echt}
        />
      ) : (
        /*
         * **Kein Rahmen um einen Dienst, von dem wir wissen, dass er ihn
         * verbietet.**
         *
         * Am 05.09.2026 am Piloten gemessen: Nextcloud und Paperless senden
         * `X-Frame-Options: SAMEORIGIN`, Bulwark `DENY`. Ein iframe darauf
         * bliebe leer, und die Shell könnte nicht einmal sagen warum — der
         * Inhalt ist fremder Herkunft, es gibt kein Ereignis für „blockiert".
         *
         * `03` verlangt für diesen Fall „lesbarer Hinweis mit Verweis auf die
         * eigene Adresse, **kein leerer Kasten**". Wenn wir den Fall schon
         * kennen, ist der ehrliche Weg, gar nicht erst zu rahmen: Ein Kasten,
         * der beim Laden scheitert, sieht nach einem Fehler aus, und die
         * Fehlersuche beginnt dann im Portal.
         */
        <div className="karte" data-hinweis="nicht-rahmbar">
          <h2 style={{ marginTop: 0 }}>{system} lässt sich nicht einbetten</h2>
          <p>
            Der Dienst sendet eine Rahmensperre —{" "}
            <code>X-Frame-Options</code> —, und das ist seine Vorgabe, kein
            Fehler. ADR-040 sieht die Einbettung vor; ob {system} dafür
            umkonfiguriert wird, ist eine offene Frage und keine, die diese
            Ansicht beantwortet.
          </p>
          <p>
            <a href={adresse} target="_blank" rel="noopener noreferrer">
              {system} in einem neuen Tab öffnen
            </a>
          </p>
          <p className="leise">
            Der Rückweg ist dann der Zurück-Knopf des Browsers oder dieser Tab —
            das ist der Preis, den die Sperre kostet.
          </p>
        </div>
      )}
    </>
  );
}

const ANSICHTEN: Record<string, () => ReactElement> = {
  "/eingang": Eingang,
  "/auftraege": Auftragsliste,
  "/kunden": Kundenliste,
  "/kalender": Kalender,
  "/aufgaben": Aufgaben,
  "/dateien": Dateien,
  "/dokumente": Dokumente,
  "/kommunikation": Kommunikation,
  "/wissen": Wissen,
  "/assistent": Assistent,
  "/administration": Administration,
};

/**
 * Bereiche mit einer Detailansicht darunter.
 *
 * `07` §7.5 gibt der Auftragsakte eine stabile Portal-ID; im Adressraum ist
 * das die Auftragsnummer — die Nummer, die am Telefon genannt wird, und nicht
 * die Vikunja-Projekt-ID. `04` §4.2 verbietet fremde numerische IDs im
 * oeffentlichen Link, und eine Auftragsnummer landet regelmaessig in einer
 * Mail an den Kunden.
 */
const DETAILS: Record<string, () => ReactElement> = {
  "/auftraege": Auftragsakte,
  "/kunden": Kundenakte,
};

function App({ vorab }: { readonly vorab?: PortalKonfiguration }) {
  // Lazy, nicht als Wert: Beide fragen `window` ab, und das gehört nicht in
  // jeden Render.
  const [wahl, setzeWahl] = useState<Wahl>(anfangsWahl);
  const [geraet, setzeGeraet] = useState<Erscheinung>(vomGeraet);

  // Die Wahl überlebt das Neuladen. Ohne das stellt ein Monteur sie jeden
  // Morgen neu ein - und stellt sie irgendwann nicht mehr ein.
  const waehle = useCallback((neu: Wahl) => {
    setzeWahl(neu);
    merken(neu);
  }, []);

  // Das Gerät kann während der Schicht umschalten - Sonnenuntergang,
  // Akkusparmodus. Ohne dieses Abonnement gälte die Automatik nur bis zum
  // ersten Rendern, also ausgerechnet nicht in dem Moment, für den sie da ist.
  useEffect(() => beiGeraetewechsel(setzeGeraet), []);

  const erscheinung = wirksame(wahl, geraet);

  /*
   * **Die Konfiguration ist beim ersten Rendern schon da.**
   *
   * Sie wird vor `createRoot` geholt, nicht in einem Effekt danach. Der
   * Unterschied ist nicht Geschmack: Ein Effekt, der ueber das Netz nachlaedt,
   * laesst die Anwendung fuer einen Moment ohne Konfiguration laufen — und
   * damit ohne die Entscheidung, ob jemand angemeldet sein muss. Am
   * 07.09.2026 war das messbar: Ein Ladezustand an dieser Stelle liess sieben
   * Oberflaechenpruefungen unter Last in Zeitueberschreitungen laufen, waehrend
   * jede einzeln bestand.
   *
   * Der Effekt darunter bleibt: Er holt sie erneut, wenn die Anwendung ohne
   * Vorabwert gestartet wurde.
   */
  const [konfiguration, setzeKonfiguration] =
    useState<PortalKonfiguration>(vorab ?? OHNE_KONFIGURATION);
  const [hinweis, setzeHinweis] = useState<string | null>(null);


  useEffect(() => {
    let abgebrochen = false;
    void ladeKonfiguration().then(({ konfiguration: k, fehlend, fehler }) => {
      if (abgebrochen) return;
      setzeKonfiguration(k);
      /*
       * **Die Dienstadressen kommen aus derselben Konfiguration.**
       *
       * ADR-044: ein Build für alle Kunden, Konfiguration zur Laufzeit. Wohin
       * ein Klick auf eine Datei führt, ist damit eine Kundeneinstellung und
       * keine Bauzeitentscheidung — im Piloten das laufende Nextcloud, im
       * lokalen Demostand nichts, und dann bleibt es bei den Attrappen.
       *
       * Steht hier nichts, ist das kein Fehler: Der Demostand ohne
       * Kundenstack ist der Regelfall beim Entwickeln.
       */
      setzeDienstadressen(k.domains as Record<string, string>);
      if (fehler) setzeHinweis(`Die Konfiguration wurde nicht geladen (${fehler}).`);
      else if (fehlend.length) {
        setzeHinweis(`In der Konfiguration fehlt: ${fehlend.join(", ")}.`);
      }
    });
    return () => { abgebrochen = true; };
  }, []);

  // Die Marke gilt je Erscheinung. Was nicht gesetzt ist, bleibt beim Token —
  // `cssVariablen` lässt leere Angaben absichtlich fallen.
  const marke = erscheinung === "dunkel"
    ? { akzent: konfiguration.branding.akzentDunkel,
        aufAkzent: konfiguration.branding.aufAkzentDunkel }
    : { akzent: konfiguration.branding.akzent,
        aufAkzent: konfiguration.branding.aufAkzent };

  // Die Erscheinung steht auch am Wurzelelement, nicht nur als Variablensatz.
  // Fremde Oberflächen im Rahmen bekommen ihr Theme über `fremdTheme`, aber
  // die Shell selbst braucht einen Haken, an dem eine Stilregel hängen kann -
  // etwa `color-scheme`, damit Scrollbalken und Formularelemente des Browsers
  // mitziehen. Ohne das bleibt der Scrollbalken im dunklen Bild hell.
  useEffect(() => {
    document.documentElement.dataset.erscheinung = erscheinung;
    document.documentElement.style.colorScheme =
      erscheinung === "dunkel" ? "dark" : "light";
  }, [erscheinung]);

  // Die Rollen kommen jetzt aus der Sitzung. Der Kommentar bei
  // `ROLLEN_OHNE_ANMELDUNG` bleibt richtig — eine angenommene Rolle waere
  // gefaehrlich —, aber die Antwort darauf ist nicht die leere Liste, sondern
  // eine **sichtbar gewaehlte** Rolle. Mit der leeren waren Administration und
  // Partnersicht nie erreichbar, also zwei der sechs Rollen aus `07` §7.2 nie
  // beurteilbar.
  const { person } = useSitzung();

  return (
    <Anmeldetor konfiguration={konfiguration} kinder={(lage: Anmeldelage) => {
      /*
       * **Angemeldet schlaegt gewaehlt.**
       *
       * Wo eine echte Anmeldung vorliegt, kommen die Rollen aus ihren
       * Gruppen — nicht aus der Personenwahl des Demostands. Beides
       * nebeneinander bestehen zu lassen waere die gefaehrlichste Fassung:
       * Ein Umschalter, der neben einer echten Anmeldung weiter wirkt,
       * verspricht eine Rechtepruefung, die es nicht gibt.
       *
       * Verworfene Gruppen werden **genannt**. `rollenAusGruppen` liefert
       * sie mit, und der Grund steht dort: „keine Rolle" und „Rolle nicht
       * angekommen" sehen sonst gleich aus.
       */
      const befund = lage.wer
        ? rollenAusGruppen(lage.wer.gruppen)
        : undefined;
      const rollen = befund ? befund.rollen : person.rollen;

      return (
    <BrowserRouter>
      {/* Die Token als CSS-Variablen. Erzeugt, nicht abgeschrieben - ADR-040,
          Auflage 4. */}
      <style>{`:root {\n${cssVariablen(erscheinung, marke)}\n}`}</style>
      <Routes>
        <Route
          element={
            <Shell
              wahl={wahl}
              beiWahl={waehle}
              merkmale={konfiguration.features}
              rollen={rollen}
              /*
               * **Angemeldet ersetzt gewaehlt, es steht nicht daneben.**
               *
               * Der Umschalter des Demonstrationsstands neben einer echten
               * Anmeldung saehe aus wie ein Rollenwechsel und waere keiner:
               * Die Rollen kommen dann aus dem Token. Wer damit die Sicht
               * wechselt, glaubt eine Rechtepruefung zu sehen, die es nicht
               * gibt.
               */
              kopfZusatz={
                lage.wer && lage.abmelden ? (
                  <AngemeldetAls wer={lage.wer} beiAbmelden={lage.abmelden} />
                ) : (
                  <Personenwahl />
                )
              }
              /*
               * **Die Stufe entscheidet, nicht der Stackname.**
               *
               * Der erste Entwurf prüfte `stackName` — und der steht auch im
               * Entwicklungsstand (`entwicklung`). Das Band wäre also lokal
               * immer erschienen, und was immer dasteht, wird weggesehen;
               * dann steht es auch beim Kunden umsonst.
               *
               * `stage` unterscheidet sauber: `dev` beim Entwickeln, `pilot`
               * und `produktion` beim Kunden. Aufgefallen beim Messen, nicht
               * beim Schreiben.
               */
              band={
                konfiguration.stage !== "dev" && konfiguration.stackName
                  ? <Demoband stackName={konfiguration.stackName} />
                  : null
              }
              neuInhalt={<NeuerAuftragImDialog />}
            />
          }
        >
          <Route
            index
            element={
              <>
                {hinweis && <Konfigurationshinweis text={hinweis} />}
                <Dashboard />
              </>
            }
          />
          <Route path="rahmenprobe/:welche" element={<Rahmenprobe />} />
          <Route path="proben" element={<Proben />} />
          <Route path="bauteile" element={<Bauteilprobe />} />
          <Route path="fehlerprobe" element={<Fehlerprobe />} />
          {/* Routen nur für das, was auch im Menü steht. Ein abgeschaltetes
              Merkmal, das über die Adresszeile doch erreichbar bleibt, wäre
              kein Versehen mehr, sondern eine Hintertür — und dieselbe
              Konfiguration, die den Punkt verbirgt, muss auch den Weg
              schließen. */}
          {/* Die Suche hat in `07` §7.3 keinen Navigationspunkt, wohl aber ein
              Paket (`PORTAL-003`). Sie bekommt deshalb eine Adresse, aber
              keinen Menueeintrag — die Liste der zwoelf Punkte dort zu
              erweitern waere eine Aenderung an der Spezifikation, und die
              gehoert nicht nebenbei gemacht (`AGENTS.md`). */}
          <Route path="suche" element={<Suche />} />
          {/*
            Der Sprung ins Quellsystem. Eigene Route und kein Dialog: Ein
            gerahmter Fremddienst braucht die volle Höhe, und die Adresse muss
            teilbar sein — `07` §7.5 gibt der Akte eine stabile Portal-ID, und
            derselbe Gedanke gilt für das, was daran hängt.
          */}
          <Route path="oeffnen/:kennung" element={<Quelle />} />
          {/* Wie die Suche: eigenes Paket (`MAINT-001`), aber kein
              Navigationspunkt in `07` §7.3. Erreichbar aus den Aufgaben und
              der Kundenakte, wo die Frage entsteht. */}
          <Route path="wartung" element={<Wartung />} />
          {/* Dasselbe wie Suche und Wartung: eigenes Paket (`EINV-001`),
              kein Navigationspunkt in `07` §7.3. Der Weg dorthin führt
              über den Eingangskorb, wo die Rechnungen ankommen. */}
          <Route path="erechnungen" element={<ERechnungen />} />
          <Route path="uebergabe" element={<Uebergabe />} />

          {sichtbareNavigation(konfiguration.features, rollen)
            .filter((p) => p.pfad !== "/")
            .flatMap((punkt) => {
              const Ansicht = ANSICHTEN[punkt.pfad];
              const Detail = DETAILS[punkt.pfad];
              const routen = [
                <Route
                  key={punkt.pfad}
                  path={punkt.pfad.slice(1)}
                  element={Ansicht ? <Ansicht /> : <Platzhalter was={punkt.text} />}
                />,
              ];
              if (Detail) {
                routen.push(
                  <Route
                    key={`${punkt.pfad}/:nummer`}
                    path={`${punkt.pfad.slice(1)}/:nummer`}
                    element={<Detail />}
                  />,
                );
              }
              return routen;
            })}
          <Route path="*" element={<Unbekannt />} />
        </Route>
      </Routes>
    </BrowserRouter>
      );
    }} />
  );
}

/**
 * Der Austauschdienst liegt beim Portal selbst.
 *
 * `location.origin` statt der fest eingetragenen Vorgabe: `pnpm demo` weicht
 * bei belegtem Port auf einen anderen aus, und dann zeigte die Vorgabe ins
 * Leere. Die eigene Herkunft stimmt immer.
 */
setzeAustauschadresse(`${window.location.origin}/demo-austausch`);

const wurzel = document.getElementById("wurzel");
if (!wurzel) throw new Error("Kein Element #wurzel in index.html");

/*
 * **Erst die Konfiguration, dann die Anwendung.**
 *
 * Sie entscheidet, ob eine Anmeldung verlangt wird (ADR-044). Sie nachzuladen
 * hiesse, diese Entscheidung erst zu treffen, wenn die Anwendung schon laeuft.
 */
const { konfiguration: vorab } = await ladeKonfiguration();

createRoot(wurzel).render(
  <StrictMode>
    {/* Der Sitzungsrahmen liegt **ausserhalb** von `App`, weil `App` selbst
        schon `useSitzung()` aufruft — fuer die Rollen, die ueber die
        sichtbaren Navigationspunkte entscheiden. Ein Anbieter kann seinen
        eigenen Verbraucher nicht enthalten. */}
    <SitzungsRahmen>
      <App vorab={vorab} />
    </SitzungsRahmen>
  </StrictMode>,
);
